import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, Search, Loader2 } from 'lucide-react';
import { getOrder } from '../lib/api';

const TrackOrderPage = () => {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState(searchParams.get('orderId') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOrder = async (id) => {
    if (!id.trim()) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const data = await getOrder(id.trim());
      setOrder(data);
    } catch (e) {
      if (e?.response?.status === 404) setError('Order not found. Please double-check your order ID.');
      else setError('Could not fetch order. Try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get('orderId');
    if (initial) fetchOrder(initial);
    // eslint-disable-next-line
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOrder(orderId);
  };

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="max-w-[800px] mx-auto px-4 lg:px-8 py-16">
        <Package className="text-[#909090] mb-4" size={36} />
        <h1 className="text-4xl font-bold mb-3">Track Your Order</h1>
        <p className="text-zinc-400 mb-8">Enter your order ID below to see the latest shipping status.</p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="e.g. HH-123456"
            className="flex-1 bg-zinc-950 border border-zinc-800 text-white px-4 py-3 rounded focus:outline-none focus:border-[#909090]"
          />
          <button disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-7 py-3 rounded transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18}/> : <Search size={18} />} Track
          </button>
        </form>

        {error && <p className="mt-6 text-red-400 text-sm">{error}</p>}

        {order && (
          <div className="mt-10 bg-zinc-950 border border-zinc-900 rounded p-6">
            <p className="text-sm text-zinc-400 mb-1">Order #{order.orderId}</p>
            <p className="text-2xl font-bold text-[#909090] mb-1">{order.stage}</p>
            <p className="text-xs text-zinc-500 mb-6">Placed on {new Date(order.createdAt).toLocaleString()}</p>
            <ol className="space-y-4 mb-6">
              {order.steps?.map((s, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${s.done ? 'bg-[#909090]' : 'bg-zinc-700'} ${s.current ? 'ring-4 ring-[#909090]/30' : ''}`} />
                  <span className={s.done ? 'text-white' : 'text-zinc-500'}>{s.label}</span>
                </li>
              ))}
            </ol>
            <div className="border-t border-zinc-900 pt-4">
              <p className="text-sm text-zinc-400 mb-2">Items in this order:</p>
              <ul className="text-sm space-y-1">
                {order.items?.map((it, i) => (
                  <li key={i} className="flex justify-between text-zinc-300">
                    <span>{it.name} × {it.qty}</span>
                    <span className="text-[#909090]">€ {(it.price * it.qty).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 flex justify-between font-semibold border-t border-zinc-900 pt-3">
                <span>Total</span>
                <span className="text-[#909090]">€ {order.total.toFixed(2)}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;
